import { useId } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { cn } from "@/shared/lib/cn";

export type PaginationBarLabels = {
  showing: string;
  to: string;
  of: string;
  entries: string;
  previous: string;
  next: string;
};

type PaginationBarProps = {
  page: number;
  totalPages: number;
  start: number;
  end: number;
  totalItems: number;
  items: (number | "ellipsis")[];
  onPageChange: (page: number) => void;
  labels: PaginationBarLabels;
  className?: string;
};

const arrowClass =
  "grid h-8 w-8 shrink-0 place-items-center rounded-lg text-muted-foreground transition-[background-color,color,transform] duration-150 hover:bg-card hover:text-foreground active:scale-[0.96] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:pointer-events-none disabled:opacity-35";

/**
 * Bas de tableau : rappel des lignes affichées à gauche, groupe compact de pages à droite.
 * La page active est une pastille lime qui glisse d'un numéro à l'autre.
 */
export function PaginationBar({ page, totalPages, start, end, totalItems, items, onPageChange, labels, className }: PaginationBarProps) {
  const pillId = useId();
  const reduceMotion = useReducedMotion();

  return (
    <div className={cn("flex flex-wrap items-center justify-between gap-3 border-t border-border px-5 py-4 text-sm text-muted-foreground", className)}>
      <p className="tabular-nums">
        {labels.showing}{" "}
        <span className="font-semibold text-foreground">
          {totalItems === 0 ? 0 : start + 1}–{end}
        </span>{" "}
        {labels.of} <span className="font-semibold text-foreground">{totalItems.toLocaleString("fr-FR")}</span> {labels.entries}
      </p>

      <nav aria-label="Pagination" className="flex max-w-full items-center gap-1 overflow-x-auto rounded-xl border border-border bg-card-muted/60 p-1">
        <button
          type="button"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
          aria-label={labels.previous}
          title={labels.previous}
          className={arrowClass}
        >
          <ChevronLeft size={16} strokeWidth={2} aria-hidden="true" />
        </button>

        {items.map((item, index) =>
          item === "ellipsis" ? (
            <span key={`ellipsis-${index}`} className="grid h-8 w-6 shrink-0 place-items-center text-muted" aria-hidden="true">
              …
            </span>
          ) : (
            <button
              key={item}
              type="button"
              onClick={() => onPageChange(item)}
              aria-current={item === page ? "page" : undefined}
              aria-label={`Page ${item}`}
              className={cn(
                "relative isolate grid h-8 min-w-8 shrink-0 place-items-center rounded-lg px-2 text-[13px] font-semibold tabular-nums transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
                item === page ? "text-accent-foreground" : "text-muted-foreground hover:bg-card hover:text-foreground",
              )}
            >
              {item === page ? (
                <motion.span
                  layoutId={pillId}
                  className="absolute inset-0 -z-10 rounded-lg bg-accent shadow-[0_2px_8px_-2px_rgb(166_242_27/0.6)]"
                  transition={reduceMotion ? { duration: 0 } : { type: "spring", duration: 0.35, bounce: 0.15 }}
                />
              ) : null}
              {item}
            </button>
          ),
        )}

        <button
          type="button"
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
          aria-label={labels.next}
          title={labels.next}
          className={arrowClass}
        >
          <ChevronRight size={16} strokeWidth={2} aria-hidden="true" />
        </button>
      </nav>
    </div>
  );
}
